import axios from 'axios';
import { debug } from '@/utils/logger';
import { isBannedUserError } from '@/api/auth';
import { isAccountLinkRequiredError, isEmailUnverifiedError } from '@/lib/accountLinking';
import { isDefinitiveAuthFailure } from '@/lib/authFailure';

const PROFILE_SYNC_DELAYS_MS = [1000, 2000];

export function isNonRetryableProfileSyncError(err: unknown): boolean {
  if (isBannedUserError(err)) return true;
  if (isAccountLinkRequiredError(err) || isEmailUnverifiedError(err)) return true;
  const errObj = err as Record<string, unknown>;
  if (errObj?.isBanned || errObj?.isDeleted) return true;
  if (axios.isAxiosError(err) && err.response?.status === 403) {
    const errorCode = (err.response.data as { error?: string })?.error;
    if (errorCode === 'Account banned' || errorCode === 'Account deleted') return true;
  }
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    if (isDefinitiveAuthFailure(status, err.response?.data)) return true;
  }
  return false;
}

export async function runWithProfileSyncRetry<T>(
  operation: () => Promise<T>,
  label = 'profileSync',
): Promise<T> {
  const maxAttempts = PROFILE_SYNC_DELAYS_MS.length + 1;
  let lastError: unknown;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      return await operation();
    } catch (err: unknown) {
      lastError = err;
      if (isNonRetryableProfileSyncError(err)) {
        throw err;
      }
      const delay = PROFILE_SYNC_DELAYS_MS[attempt];
      if (delay == null) {
        break;
      }
      debug(`[${label}] Profile sync failed, retry ${attempt + 1}/${maxAttempts - 1} after ${delay}ms`);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}
